import { useEffect, useState } from 'react'
import {
  Clock,
  CheckCircle,
  AlertTriangle,
  AlertCircle,
  RefreshCw,
  Package,
  X,
  History,
} from 'lucide-react'
import { useTranslation } from 'react-i18next'
import api from '../services/api'
import { getApiErrorKey } from '../utils/apiError'
import type { RevisionSchedule, EquipmentItem, EquipmentRevision } from '../types'
import { formatDate } from '../utils/helpers'

function StatusBadge({ status }: { status: RevisionSchedule['status'] }) {
  const { t } = useTranslation()
  if (status === 'overdue') {
    return (
      <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-semibold bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400">
        <AlertCircle size={12} />
        {t('revisions.statusOverdue')}
      </span>
    )
  }
  if (status === 'due') {
    return (
      <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-semibold bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400">
        <AlertTriangle size={12} />
        {t('revisions.statusDue')}
      </span>
    )
  }
  return (
    <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-semibold bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400">
      <CheckCircle size={12} />
      {t('revisions.statusOk')}
    </span>
  )
}

function EquipmentStatusBadge({
  status,
  nextRevision,
}: {
  status: EquipmentItem['status']
  nextRevision?: string
}) {
  const { t } = useTranslation()
  if (status === 'end_of_life') {
    return (
      <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-semibold bg-gray-100 text-gray-500 dark:bg-gray-700 dark:text-gray-400">
        {t('parc.statusEndOfLife')}
      </span>
    )
  }
  if (status === 'retired') {
    return (
      <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-semibold bg-gray-100 text-gray-400 dark:bg-gray-700 dark:text-gray-500">
        {t('parc.statusRetired')}
      </span>
    )
  }
  // active — check overdue/due based on next_revision_at
  if (nextRevision) {
    const diff = (new Date(nextRevision).getTime() - Date.now()) / 86400000
    if (diff < 0) {
      return (
        <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-semibold bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400">
          <AlertCircle size={12} />
          {t('revisions.statusOverdue')}
        </span>
      )
    }
    if (diff <= 30) {
      return (
        <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-semibold bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400">
          <AlertTriangle size={12} />
          {t('revisions.statusDue')}
        </span>
      )
    }
  }
  return (
    <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-semibold bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400">
      <CheckCircle size={12} />
      {t('revisions.statusOk')}
    </span>
  )
}

export default function Revisions() {
  const { t } = useTranslation()
  const [activeTab, setActiveTab] = useState<'schedules' | 'parc'>('schedules')

  // Schedules tab state
  const [revisions, setRevisions] = useState<RevisionSchedule[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [acknowledging, setAcknowledging] = useState<number | null>(null)

  // Parc tab state
  const [parc, setParc] = useState<EquipmentItem[]>([])
  const [parcLoading, setParcLoading] = useState(false)
  const [parcError, setParcError] = useState<string | null>(null)

  // Revise modal
  const [reviseItem, setReviseItem] = useState<EquipmentItem | null>(null)
  const [reviseDate, setReviseDate] = useState('')
  const [reviseNotes, setReviseNotes] = useState('')
  const [reviseSaving, setReviseSaving] = useState(false)
  const [reviseError, setReviseError] = useState<string | null>(null)

  // History modal
  const [historyItem, setHistoryItem] = useState<EquipmentItem | null>(null)
  const [history, setHistory] = useState<EquipmentRevision[]>([])
  const [historyLoading, setHistoryLoading] = useState(false)

  useEffect(() => {
    loadRevisions()
  }, [])

  useEffect(() => {
    if (activeTab === 'parc' && parc.length === 0 && !parcLoading) {
      loadParc()
    }
  }, [activeTab, parc.length, parcLoading])

  const loadRevisions = async () => {
    setLoading(true)
    try {
      const data = await api.getRevisions()
      setRevisions(data)
      setError(null)
    } catch (err: unknown) {
      const apiErr = err as { response?: { data?: { error?: string } } }
      setError(getApiErrorKey(apiErr, 'revisions.loadError'))
    } finally {
      setLoading(false)
    }
  }

  const loadParc = async () => {
    setParcLoading(true)
    try {
      const data = await api.getParc()
      setParc(data)
      setParcError(null)
    } catch (err: unknown) {
      const apiErr = err as { response?: { data?: { error?: string } } }
      setParcError(getApiErrorKey(apiErr, 'parc.loadError'))
    } finally {
      setParcLoading(false)
    }
  }

  const handleAcknowledge = async (rev: RevisionSchedule) => {
    if (!window.confirm(t('revisions.confirmAck', { label: rev.label }))) return
    setAcknowledging(rev.id)
    try {
      const updated = await api.acknowledgeRevision(rev.id)
      setRevisions((prev) => prev.map((r) => (r.id === updated.id ? { ...r, ...updated } : r)))
    } catch {
      alert(t('revisions.ackError'))
    } finally {
      setAcknowledging(null)
    }
  }

  const openRevise = (item: EquipmentItem) => {
    setReviseItem(item)
    setReviseDate(new Date().toISOString().split('T')[0])
    setReviseNotes('')
    setReviseError(null)
  }

  const handleRevise = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!reviseItem || !reviseDate) return
    setReviseSaving(true)
    setReviseError(null)
    try {
      const updated = await api.reviseEquipmentItem(
        reviseItem.id,
        reviseDate,
        reviseNotes || undefined
      )
      setParc((prev) => prev.map((i) => (i.id === updated.id ? { ...i, ...updated } : i)))
      setReviseItem(null)
    } catch (err: unknown) {
      const apiErr = err as { response?: { data?: { error?: string } } }
      setReviseError(getApiErrorKey(apiErr, 'parc.reviseError'))
    } finally {
      setReviseSaving(false)
    }
  }

  const openHistory = async (item: EquipmentItem) => {
    setHistoryItem(item)
    setHistory([])
    setHistoryLoading(true)
    try {
      const data = await api.getParcItemHistory(item.id)
      setHistory(data)
    } catch {
      // show empty
    } finally {
      setHistoryLoading(false)
    }
  }

  const tabs = [
    { key: 'schedules' as const, label: t('parc.tabSchedules'), icon: Clock },
    { key: 'parc' as const, label: t('parc.tabParc'), icon: Package },
  ]

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white flex items-center gap-2">
            <Clock className="h-6 w-6 text-blue-600" />
            {t('revisions.title')}
          </h1>
          <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">{t('revisions.subtitle')}</p>
        </div>
        <button
          onClick={activeTab === 'schedules' ? loadRevisions : loadParc}
          className="p-2 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700 text-gray-500 dark:text-gray-400"
          title={t('common.refresh', 'Rafraîchir')}
        >
          <RefreshCw size={18} />
        </button>
      </div>

      {/* Tabs */}
      <div className="border-b border-gray-200 dark:border-gray-700">
        <nav className="flex gap-1">
          {tabs.map(({ key, label, icon: Icon }) => (
            <button
              key={key}
              onClick={() => setActiveTab(key)}
              className={`flex items-center gap-2 px-4 py-2.5 text-sm font-medium border-b-2 transition-colors ${
                activeTab === key
                  ? 'border-blue-600 text-blue-600 dark:text-blue-400'
                  : 'border-transparent text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200'
              }`}
            >
              <Icon size={16} />
              {label}
            </button>
          ))}
        </nav>
      </div>

      {/* Schedules tab */}
      {activeTab === 'schedules' && (
        <>
          {error && (
            <div className="p-4 bg-red-50 border border-red-200 rounded-lg text-red-700 dark:bg-red-900/20 dark:border-red-800 dark:text-red-400">
              {t(error)}
            </div>
          )}
          {loading ? (
            <div className="flex items-center justify-center h-64">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600" />
            </div>
          ) : revisions.length === 0 ? (
            <div className="text-center py-16 text-gray-500 dark:text-gray-400">
              <Clock className="mx-auto h-12 w-12 mb-4 opacity-40" />
              <p className="text-lg">{t('revisions.noRevisions')}</p>
            </div>
          ) : (
            <div className="bg-white dark:bg-gray-800 rounded-xl shadow overflow-hidden">
              {/* Mobile card layout */}
              <ul className="divide-y divide-gray-200 dark:divide-gray-700 sm:hidden">
                {revisions.map((rev) => (
                  <li key={rev.id} className="p-4 space-y-2">
                    <div className="flex items-start justify-between gap-2">
                      <div className="min-w-0">
                        <p className="font-medium text-gray-900 dark:text-white text-sm">
                          {rev.label}
                        </p>
                        <p className="text-xs text-gray-500 dark:text-gray-400">
                          {rev.product_ref}
                        </p>
                      </div>
                      <StatusBadge status={rev.status} />
                    </div>
                    <div className="flex items-center justify-between gap-2 text-xs text-gray-500 dark:text-gray-400">
                      <span>
                        {t('revisions.colLastPurchase')} :{' '}
                        {rev.last_purchased_at
                          ? formatDate(rev.last_purchased_at)
                          : t('revisions.noPurchase')}
                      </span>
                      {rev.next_revision_at && (
                        <span>
                          {t('revisions.colNextRevision')} : {formatDate(rev.next_revision_at)}
                        </span>
                      )}
                    </div>
                    {rev.status !== 'ok' && !rev.acknowledged_at ? (
                      <button
                        onClick={() => handleAcknowledge(rev)}
                        disabled={acknowledging === rev.id}
                        className="inline-flex items-center gap-1 px-3 py-1.5 text-xs font-medium rounded-lg bg-blue-600 text-white hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                      >
                        {acknowledging === rev.id ? (
                          <RefreshCw size={12} className="animate-spin" />
                        ) : (
                          <CheckCircle size={12} />
                        )}
                        {t('revisions.acknowledge')}
                      </button>
                    ) : rev.acknowledged_at ? (
                      <span className="text-xs text-green-600 dark:text-green-400 flex items-center gap-1">
                        <CheckCircle size={12} />
                        {t('revisions.acknowledged')}
                      </span>
                    ) : null}
                  </li>
                ))}
              </ul>
              {/* Desktop table layout */}
              <div className="hidden sm:block">
                <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
                  <thead className="bg-gray-50 dark:bg-gray-900">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                        {t('revisions.colProduct')}
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                        {t('revisions.colLastPurchase')}
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                        {t('revisions.colNextRevision')}
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                        {t('revisions.colStatus')}
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                        {t('revisions.colAction')}
                      </th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                    {revisions.map((rev) => (
                      <tr
                        key={rev.id}
                        className="hover:bg-gray-50 dark:hover:bg-gray-700/50 transition-colors"
                      >
                        <td className="px-6 py-4">
                          <div className="font-medium text-gray-900 dark:text-white">
                            {rev.label}
                          </div>
                          <div className="text-xs text-gray-500 dark:text-gray-400">
                            {rev.product_ref}
                          </div>
                        </td>
                        <td className="px-6 py-4 text-sm text-gray-600 dark:text-gray-300">
                          {rev.last_purchased_at
                            ? formatDate(rev.last_purchased_at)
                            : t('revisions.noPurchase')}
                        </td>
                        <td className="px-6 py-4 text-sm text-gray-600 dark:text-gray-300">
                          {rev.next_revision_at ? formatDate(rev.next_revision_at) : '—'}
                        </td>
                        <td className="px-6 py-4">
                          <StatusBadge status={rev.status} />
                        </td>
                        <td className="px-6 py-4">
                          {rev.status !== 'ok' && !rev.acknowledged_at ? (
                            <button
                              onClick={() => handleAcknowledge(rev)}
                              disabled={acknowledging === rev.id}
                              className="inline-flex items-center gap-1 px-3 py-1.5 text-xs font-medium rounded-lg bg-blue-600 text-white hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                            >
                              {acknowledging === rev.id ? (
                                <RefreshCw size={12} className="animate-spin" />
                              ) : (
                                <CheckCircle size={12} />
                              )}
                              {t('revisions.acknowledge')}
                            </button>
                          ) : rev.acknowledged_at ? (
                            <span className="text-xs text-green-600 dark:text-green-400 flex items-center gap-1">
                              <CheckCircle size={12} />
                              {t('revisions.acknowledged')}
                            </span>
                          ) : null}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </>
      )}

      {/* Parc tab */}
      {activeTab === 'parc' && (
        <>
          {parcError && (
            <div className="p-4 bg-red-50 border border-red-200 rounded-lg text-red-700 dark:bg-red-900/20 dark:border-red-800 dark:text-red-400">
              {t(parcError)}
            </div>
          )}
          {parcLoading ? (
            <div className="flex items-center justify-center h-64">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600" />
            </div>
          ) : parc.length === 0 ? (
            <div className="text-center py-16 text-gray-500 dark:text-gray-400">
              <Package className="mx-auto h-12 w-12 mb-4 opacity-40" />
              <p className="text-lg">{t('parc.noEquipment')}</p>
            </div>
          ) : (
            <div className="bg-white dark:bg-gray-800 rounded-xl shadow overflow-hidden">
              {/* Mobile card layout */}
              <ul className="divide-y divide-gray-200 dark:divide-gray-700 sm:hidden">
                {parc.map((item) => (
                  <li
                    key={item.id}
                    className="p-4 space-y-2 cursor-pointer"
                    onClick={() => openHistory(item)}
                  >
                    <div className="flex items-start justify-between gap-2">
                      <div className="min-w-0">
                        <p className="font-medium text-gray-900 dark:text-white text-sm">
                          {item.display_label}
                        </p>
                        <p className="text-xs text-gray-500 dark:text-gray-400">
                          {item.product_ref}
                        </p>
                        {item.serial_number && (
                          <p className="text-xs text-gray-400 dark:text-gray-500 font-mono">
                            {item.serial_number}
                          </p>
                        )}
                      </div>
                      <EquipmentStatusBadge
                        status={item.status}
                        nextRevision={item.next_revision_at}
                      />
                    </div>
                    <div className="flex items-center justify-between gap-2 text-xs text-gray-500 dark:text-gray-400">
                      <div className="space-y-0.5">
                        {item.last_revised_at && (
                          <p>
                            {t('parc.colLastRevision')} : {formatDate(item.last_revised_at)}
                          </p>
                        )}
                        {item.next_revision_at && (
                          <p>
                            {t('parc.colNextRevision')} : {formatDate(item.next_revision_at)}
                          </p>
                        )}
                      </div>
                      {item.status === 'active' && (
                        <button
                          onClick={(e) => {
                            e.stopPropagation()
                            openRevise(item)
                          }}
                          className="inline-flex items-center gap-1 px-2.5 py-1 text-xs font-medium rounded-lg bg-blue-600 text-white hover:bg-blue-700 transition-colors flex-shrink-0"
                        >
                          <CheckCircle size={12} />
                          {t('parc.revise')}
                        </button>
                      )}
                    </div>
                  </li>
                ))}
              </ul>
              {/* Desktop table layout */}
              <div className="hidden sm:block">
                <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
                  <thead className="bg-gray-50 dark:bg-gray-900">
                    <tr>
                      {[
                        'parc.colLabel',
                        'parc.colSerial',
                        'parc.colPurchase',
                        'parc.colLastRevision',
                        'parc.colNextRevision',
                        'parc.colStatus',
                        'revisions.colAction',
                      ].map((key) => (
                        <th
                          key={key}
                          className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider"
                        >
                          {t(key)}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                    {parc.map((item) => (
                      <tr
                        key={item.id}
                        className="hover:bg-gray-50 dark:hover:bg-gray-700/50 transition-colors cursor-pointer"
                        onClick={() => openHistory(item)}
                      >
                        <td className="px-6 py-4">
                          <div className="font-medium text-gray-900 dark:text-white">
                            {item.display_label}
                          </div>
                          <div className="text-xs text-gray-500 dark:text-gray-400">
                            {item.product_ref}
                          </div>
                        </td>
                        <td className="px-6 py-4 text-sm text-gray-600 dark:text-gray-300">
                          {item.serial_number || '—'}
                        </td>
                        <td className="px-6 py-4 text-sm text-gray-600 dark:text-gray-300">
                          {item.purchase_date ? formatDate(item.purchase_date) : '—'}
                        </td>
                        <td className="px-6 py-4 text-sm text-gray-600 dark:text-gray-300">
                          {item.last_revised_at ? formatDate(item.last_revised_at) : '—'}
                        </td>
                        <td className="px-6 py-4 text-sm text-gray-600 dark:text-gray-300">
                          {item.next_revision_at ? formatDate(item.next_revision_at) : '—'}
                        </td>
                        <td className="px-6 py-4">
                          <EquipmentStatusBadge
                            status={item.status}
                            nextRevision={item.next_revision_at}
                          />
                        </td>
                        <td className="px-6 py-4" onClick={(e) => e.stopPropagation()}>
                          {item.status === 'active' && (
                            <button
                              onClick={() => openRevise(item)}
                              className="inline-flex items-center gap-1 px-3 py-1.5 text-xs font-medium rounded-lg bg-blue-600 text-white hover:bg-blue-700 transition-colors"
                            >
                              <CheckCircle size={12} />
                              {t('parc.revise')}
                            </button>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </>
      )}

      {/* Revise modal */}
      {reviseItem && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white dark:bg-gray-800 rounded-xl shadow-xl w-full max-w-md">
            <div className="flex items-center justify-between p-6 border-b border-gray-200 dark:border-gray-700">
              <h2 className="text-lg font-semibold text-gray-900 dark:text-white">
                {t('parc.revise')}
              </h2>
              <button
                onClick={() => setReviseItem(null)}
                className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-200"
              >
                <X size={20} />
              </button>
            </div>
            <form onSubmit={handleRevise} className="p-6 space-y-4">
              <p className="text-sm text-gray-600 dark:text-gray-300">{reviseItem.display_label}</p>
              {reviseError && (
                <div className="p-3 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm dark:bg-red-900/20 dark:border-red-800 dark:text-red-400">
                  {t(reviseError)}
                </div>
              )}
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  {t('parc.revisionDate')} *
                </label>
                <input
                  type="date"
                  value={reviseDate}
                  onChange={(e) => setReviseDate(e.target.value)}
                  required
                  className="w-full border border-gray-300 dark:border-gray-600 rounded-lg px-3 py-2 text-sm bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  {t('parc.revisionNotes')}
                </label>
                <textarea
                  value={reviseNotes}
                  onChange={(e) => setReviseNotes(e.target.value)}
                  rows={3}
                  className="w-full border border-gray-300 dark:border-gray-600 rounded-lg px-3 py-2 text-sm bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                />
              </div>
              <div className="flex justify-end gap-3 pt-2">
                <button
                  type="button"
                  onClick={() => setReviseItem(null)}
                  className="px-4 py-2 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors"
                >
                  {t('common.cancel', 'Annuler')}
                </button>
                <button
                  type="submit"
                  disabled={reviseSaving}
                  className="px-4 py-2 text-sm bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 transition-colors inline-flex items-center gap-2"
                >
                  {reviseSaving && <RefreshCw size={14} className="animate-spin" />}
                  {t('common.save', 'Enregistrer')}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* History modal */}
      {historyItem && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white dark:bg-gray-800 rounded-xl shadow-xl w-full max-w-lg">
            <div className="flex items-center justify-between p-6 border-b border-gray-200 dark:border-gray-700">
              <h2 className="text-lg font-semibold text-gray-900 dark:text-white flex items-center gap-2">
                <History size={18} className="text-blue-600" />
                {t('parc.history')}
              </h2>
              <button
                onClick={() => setHistoryItem(null)}
                className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-200"
              >
                <X size={20} />
              </button>
            </div>
            <div className="p-6">
              <p className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-4">
                {historyItem.display_label}
              </p>
              {historyLoading ? (
                <div className="flex justify-center py-8">
                  <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-600" />
                </div>
              ) : history.length === 0 ? (
                <p className="text-sm text-gray-500 dark:text-gray-400 text-center py-8">
                  {t('parc.noHistory')}
                </p>
              ) : (
                <div className="space-y-2 max-h-80 overflow-y-auto">
                  {history.map((rev) => (
                    <div
                      key={rev.id}
                      className="flex items-start gap-3 p-3 rounded-lg bg-gray-50 dark:bg-gray-700/50"
                    >
                      <CheckCircle size={16} className="text-green-500 mt-0.5 shrink-0" />
                      <div>
                        <div className="text-sm font-medium text-gray-900 dark:text-white">
                          {formatDate(rev.revised_at)}
                        </div>
                        {rev.notes && (
                          <div className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
                            {rev.notes}
                          </div>
                        )}
                        {(rev.firstname || rev.lastname) && (
                          <div className="text-xs text-gray-400 dark:text-gray-500 mt-0.5">
                            {[rev.firstname, rev.lastname].filter(Boolean).join(' ')}
                          </div>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
